import { useState, useEffect, useRef } from 'react'
import { X, HelpCircle, ChevronRight, GripVertical, BookOpen } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { articlesForPath } from '../../lib/help/helpContent'

interface Props {
  open: boolean
  onClose: () => void
}

export function HelpPanel({ open, onClose }: Props) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { role } = useAuthStore()

  // Role-aware, sourced from the shared help content model. Shows the guides
  // relevant to the page the user is currently on ("help on this page").
  const matches = articlesForPath(pathname, role).slice(0, 4)

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const openFullGuide = () => {
    onClose()
    navigate(`/help?focus=${encodeURIComponent(pathname)}`)
  }

  return (
    <>
      {/* Backdrop (mobile) */}
      <div className="fixed inset-0 z-40 bg-black/20 lg:hidden" onClick={onClose} />

      {/* Panel */}
      <div className="fixed bottom-0 right-0 z-50 lg:bottom-auto lg:top-16 w-full lg:w-80 bg-white shadow-2xl rounded-t-2xl lg:rounded-2xl border border-gray-100 flex flex-col max-h-[80vh] lg:max-h-[70vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-brand-500" />
            <span className="font-semibold text-gray-900 text-sm">Help on this page</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {matches.length > 0 ? (
            matches.map(article => (
              <div key={article.id}>
                <h3 className="font-bold text-gray-900 text-sm">{article.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{article.summary}</p>
                <ul className="space-y-1.5 mt-2">
                  {article.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <ChevronRight className="h-3.5 w-3.5 text-brand-400 mt-0.5 flex-shrink-0" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <HelpCircle className="h-8 w-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No specific help for this page yet — open the full guide below.</p>
            </div>
          )}
        </div>

        {/* Full-guide link */}
        <div className="p-3 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={openFullGuide}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-50 text-brand-700 text-sm font-semibold py-2.5 hover:bg-brand-100 transition-colors"
          >
            <BookOpen className="h-4 w-4" />
            Open the full Help guide
          </button>
        </div>
      </div>
    </>
  )
}

/** Floating draggable help button */
export function HelpButton({ onClick }: { onClick: () => void }) {
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    try {
      const saved = localStorage.getItem('help-button-position')
      if (saved) return JSON.parse(saved)
    } catch { /* ignore */ }
    return { x: window.innerWidth - 80, y: window.innerHeight - 80 }
  })
  const [isDragging, setIsDragging] = useState(false)
  const dragOffset = useRef({ x: 0, y: 0 })
  const didDrag = useRef(false)
  const btnRef = useRef<HTMLDivElement>(null)

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    didDrag.current = false
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y }
    e.preventDefault()
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    setIsDragging(true)
    didDrag.current = false
    dragOffset.current = { x: touch.clientX - position.x, y: touch.clientY - position.y }
  }

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      didDrag.current = true
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 60, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragOffset.current.y)),
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      localStorage.setItem('help-button-position', JSON.stringify(position))
    }

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      didDrag.current = true
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 60, touch.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 60, touch.clientY - dragOffset.current.y)),
      })
    }

    const handleTouchEnd = () => {
      setIsDragging(false)
      localStorage.setItem('help-button-position', JSON.stringify(position))
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTouchEnd)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isDragging, position])

  const handleClick = () => {
    if (!didDrag.current) onClick()
  }

  return (
    <div
      ref={btnRef}
      title="Drag to move"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9999,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      className="h-11 w-11 rounded-full bg-brand-500 text-white shadow-lg flex items-center justify-center hover:bg-brand-600 transition-colors select-none"
      aria-label="Help"
    >
      {isDragging
        ? <GripVertical className="h-4 w-4 opacity-70" />
        : <HelpCircle className="h-5 w-5" />}
    </div>
  )
}
